import { styled } from "@pigment-css/react";
import type { QuestionPrompts as QuestionPromptsSchema, Sentiment } from "@acto/genui-components-schema";
import { useTranslations } from "@acto/web-common/locales";
import { Card, MetaLabel, RowItem, RowList } from "@acto/taskpane/field-sales-2/components/primitives/Card";

type FramingTone = "default" | "growth";

const framingForSentiment = (sentiment: Sentiment): FramingTone => (sentiment === "positive" ? "growth" : "default");

const Toolbar = styled("header")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.fieldSalesTwo.spacing[12],
}));

const FramingTag = styled("span")<{ framing: FramingTone }>(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["13"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  padding: `${theme.fieldSalesTwo.spacing[2]} ${theme.fieldSalesTwo.spacing[10]}`,
  borderRadius: theme.fieldSalesTwo.radius.circular,
  background: theme.fieldSalesTwo.colors.neutral[200],
  color: theme.fieldSalesTwo.colors.neutral[800],
  variants: [
    {
      props: { framing: "growth" },
      style: {
        background: theme.fieldSalesTwo.colors.status.success.tint,
        color: theme.fieldSalesTwo.colors.status.success.main,
      },
    },
  ],
}));

const Items = styled(RowList)(({ theme }) => ({
  gap: theme.fieldSalesTwo.spacing[16],
}));

const Item = styled(RowItem)(({ theme }) => ({
  flexDirection: "column",
  alignItems: "stretch",
  gap: theme.fieldSalesTwo.spacing[4],
}));

const Question = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.base,
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["148"],
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const Rationale = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["14"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["168"],
  color: theme.fieldSalesTwo.colors.neutral[700],
}));

interface QuestionPromptsProps {
  part: QuestionPromptsSchema;
  sentiment: Sentiment;
}

export function QuestionPrompts({ part, sentiment }: QuestionPromptsProps) {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.questionPrompts");
  const framing = framingForSentiment(sentiment);

  return (
    <Card>
      <Toolbar>
        <MetaLabel>{t("meta")}</MetaLabel>
        {framing === "growth" && <FramingTag framing="growth">{t("growthTag")}</FramingTag>}
      </Toolbar>
      <Items role="list">
        {part.items.map((item, index) => (
          <Item key={index}>
            <Question>{item.question}</Question>
            {item.rationale && <Rationale>→ {item.rationale}</Rationale>}
          </Item>
        ))}
      </Items>
    </Card>
  );
}
